import React, { useEffect, useState, useCallback, useContext } from "react";
import DataTable, { Column, Row } from "components/DataTable";
import apiService from "services/apiService";
import AddReview from "./AddReview";
import GenericModal from "components/Modal";
import { FaEdit, FaRegTrashAlt, FaEye } from "react-icons/fa";
import GenericToaster from "components/Toaster";
import EditReview from "./EditReview";
import { epocToDate } from "util/epochTime";
import DeletePopup from "components/DeletePopup/deletePopup";
import HeaderButtons from "components/Button/HeaderButtons";
import ViewReview from "./ViewReview";
import { UserRole } from "../../util/roles";
import { useAppContext } from "contexts";
import { BannerContext } from "components/SideBar";

// ── Helpers ───────────────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const convertReviewDates = (items: any[]) => {
  if (!Array.isArray(items)) return [];
  return items.map((item) => {
    const maxLength = 30;
    const truncatedReview =
      item.review && item.review.length > maxLength
        ? `${item.review.substring(0, maxLength)}...`
        : item.review;
    return { ...item, reviewDate: epocToDate(item.reviewDate), truncatedReview };
  });
};

type ReviewRow = Record<string, unknown>;

// ── Avatar ────────────────────────────────────────────────────────────────────

export const AVATAR_COLORS = [
  "bg-blue-500", "bg-emerald-500", "bg-violet-500", "bg-orange-500",
  "bg-pink-500", "bg-teal-500", "bg-indigo-500", "bg-rose-500",
];

export const Avatar = ({ name }: { name: string }) => {
  const str = name || "?";
  const color = AVATAR_COLORS[str.charCodeAt(0) % AVATAR_COLORS.length];
  const initials = str.split("@")[0].substring(0, 2).toUpperCase();
  return (
    <div className={`w-8 h-8 rounded-full ${color} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>
      {initials}
    </div>
  );
};

// ── Action buttons ────────────────────────────────────────────────────────────

const ReviewActionButtons = ({
  row, onEdit, onView, onDelete, isAllReviews, canManage,
}: {
  row: ReviewRow;
  onEdit: (row: ReviewRow) => void;
  onView: (row: ReviewRow) => void;
  onDelete: (row: ReviewRow) => void;
  isAllReviews: boolean;
  // privileged users (admin/manager) can edit and delete their own reviews
  canManage: boolean;
}) => (
  <div className="flex items-center gap-2">
    <span
      onClick={() => onView(row)}
      className="btn-primary px-2 py-2 rounded-lg cursor-pointer"
      title="View"
    >
      <FaEye />
    </span>
    {canManage && !isAllReviews && (
      <>
        <span
          onClick={() => onEdit(row)}
          className="btn-primary px-2 py-2 rounded-lg cursor-pointer"
          title="Edit"
        >
          <FaEdit />
        </span>
        <span
          onClick={() => onDelete(row)}
          className="btn-red px-2 py-2 rounded-lg cursor-pointer"
          title="Delete"
        >
          <FaRegTrashAlt />
        </span>
      </>
    )}
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────

const ReviewList = () => {
  const { userRole, email: loggedInEmail } = useAppContext();

  // Admin and Manager have full access; User/SysAdmin see only their own reviews
  const isPrivileged =
    userRole === UserRole.ADMIN || userRole === UserRole.MANAGER;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [feedbackList, setFeedbackList] = React.useState<any[]>([]);
  const [isAddReview, setIsAddReview] = React.useState(false);
  // Default to "getAll"; getReviewList routes the actual API call by role
  const [url, setUrl] = React.useState("getAll");
  const [message, setMessage] = useState("Something went wrong");
  const [showSuccessToaster, setSuccessToaster] = useState(false);
  const [showErrorToaster, setErrorToaster] = useState(false);
  const [edit, setEdit] = useState(false);
  const [editData, setEditData] = useState<ReviewRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [isDelete, setIsDelete] = useState(false);
  const [selectedRow, setSelectedRow] = useState<ReviewRow | null>(null);
  const [view, setView] = useState(false);
  const [activeView, setActiveView] = useState("getAll");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");

  const { isBannerVisible } = useContext(BannerContext);
      
        const dynamicHeightClass = isBannerVisible
          ? "h-[calc(100vh-105px)] min-h-[300px]"
          : "h-[calc(100vh-75px)] min-h-[300px]";

  const handleView = (row: ReviewRow) => { setEditData(row); setView(true); };
  const handleEdit = (row: ReviewRow) => { setEditData(row); setEdit(true); };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDelete = (row: any) => { setIsDelete(true); setSelectedRow(row); };

  const getReviewList = async () => {
    setLoading(true);
    setActiveView(url);
    try {
      let endpoint: string;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let rawContent: any[];

      if (!isPrivileged) {
        // User (role 1): simple list, no pagination
        const res = await apiService.get("api/review");
        const apiData = res.data;
        rawContent =
          apiData && typeof apiData === "object" && "content" in apiData
            ? apiData.content
            : apiData;
        setFeedbackList(convertReviewDates(rawContent));
        setTotalItems(Array.isArray(rawContent) ? rawContent.length : 0);
        return;
      }

      if (url === "receivedByMe") {
        // No separate API — fetch full getAll list and filter by logged-in user's email
        endpoint = "api/review/getAll?page=0&size=1000";
        const res = await apiService.get(endpoint);
        const apiData = res.data;
        const all =
          apiData && typeof apiData === "object" && "content" in apiData
            ? apiData.content
            : apiData;
        rawContent = Array.isArray(all)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ? all.filter((r: any) =>
            String(r.employeeEmail || "").toLowerCase() ===
            String(loggedInEmail || "").toLowerCase()
          )
          : [];
        setFeedbackList(convertReviewDates(rawContent));
        setTotalItems(rawContent.length);
      } else {
        // "getAll" or "reviewed" — server-side paginated
        const params = new URLSearchParams();
        params.set("page", String(currentPage));
        params.set("size", String(itemsPerPage));
        if (searchQuery.trim()) params.set("search", searchQuery.trim());
        endpoint = `api/review/${url}?${params.toString()}`;
        const res = await apiService.get(endpoint);
        const apiData = res.data;
        rawContent =
          apiData && typeof apiData === "object" && "content" in apiData
            ? apiData.content
            : apiData;
        setFeedbackList(convertReviewDates(rawContent));
        setTotalItems(
          typeof apiData?.totalElements === "number"
            ? apiData.totalElements
            : Array.isArray(rawContent) ? rawContent.length : 0
        );
      }
    } catch (err) {
      console.log("Error:", err);
      setFeedbackList([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getReviewList();
  }, [url, currentPage, itemsPerPage, searchQuery]);

  const handleSuccess = (msg: string) => { setMessage(msg); setSuccessToaster(true); getReviewList(); };
  const handleError = (msg: string) => { setMessage(msg); setErrorToaster(true); };
  const closeToaster = () => { setErrorToaster(false); setSuccessToaster(false); };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const deleteRow = (id: any) => {
    apiService.delete(`api/review/${id}`)
      .then((res) => {
        if (res.status === 200) { getReviewList(); setSuccessToaster(true); setMessage("Deleted successfully"); }
        else { setErrorToaster(true); setMessage("Something went wrong"); }
      })
      .catch((err) => { console.log("Error:", err); setErrorToaster(true); setMessage("Something went wrong"); })
      .finally(() => setIsDelete(false));
  };

  const handleItemsPerPageChange = useCallback((n: number) => {
    setItemsPerPage((p) => (p === n ? p : n));
    setCurrentPage(0);
  }, []);

  const handleServerPageChange = useCallback((n: number) => { setCurrentPage(n - 1); }, []);

  const handleSearchChange = useCallback((term: string) => {
    setSearchQuery((p) => (p === term ? p : term));
    setCurrentPage(0);
  }, []);

  const showAllReviews = () => { if (!loading && url !== "getAll") { setUrl("getAll"); setCurrentPage(0); } };
  const showGivenByMe = () => { if (!loading && url !== "reviewed") { setUrl("reviewed"); setCurrentPage(0); } };
  // No separate API — fetched from getAll and filtered client-side by logged-in email
  const showMyFeedback = () => { if (!loading && url !== "receivedByMe") { setUrl("receivedByMe"); setCurrentPage(0); } };

  // ── Table columns ───────────────────────────────────────────────────────────

  const columns: Column[] = [
    {
      key: "employeeName",
      label: "Employee",
      render: (_value, row) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const r = row as any;
        return (
          <div className="flex items-center gap-2.5">
            <Avatar name={r.employeeName || r.employeeEmail || "?"} />
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {r.employeeName || String(r.employeeEmail || "").split("@")[0] || "—"}
              </p>
              {r.employeeName && (
                <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                  {String(r.employeeEmail || "")}
                </p>
              )}
            </div>
          </div>
        );
      },
    },
    { key: "truncatedReview", label: "Review", bold: true },
    { key: "reviewerName", label: "Reviewer", bold: true },
    { key: "reviewDate", label: "Review Date", bold: true },
    {
      key: "actions",
      label: "Actions",
      excludeExport: true,
      actions: (row: Row) => (
        <ReviewActionButtons
          row={row as ReviewRow}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          // Edit/Delete only available on "Given by Me" — reviews the user wrote themselves
          isAllReviews={activeView !== "reviewed"}
          canManage={isPrivileged}
        />
      ),
    },
  ];

  // ── Toggle buttons — only shown to admin / manager ─────────────────────────
  // Three views: all in system | reviews I wrote | feedback received about me

  const reviewFilterButtons = isPrivileged ? (
    <div className="flex gap-2 flex-wrap">
      <HeaderButtons
        type="button"
        variant={activeView === "getAll" ? "selected" : "inactive"}
        onClick={showAllReviews}
        disabled={loading}
        className="w-36"
      >
        All Reviews
      </HeaderButtons>
      <HeaderButtons
        type="button"
        variant={activeView === "reviewed" ? "selected" : "inactive"}
        onClick={showGivenByMe}
        disabled={loading}
        className="w-36"
      >
        Given by Me
      </HeaderButtons>
      <HeaderButtons
        type="button"
        variant={activeView === "receivedByMe" ? "selected" : "inactive"}
        onClick={showMyFeedback}
        disabled={loading}
        className="w-36"
      >
        My Feedback
      </HeaderButtons>
    </div>
  ) : undefined;

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="text-slate-900 dark:text-white bg-white dark:bg-gray-800 transition-colors duration-300">
      <DataTable
      heightClass={dynamicHeightClass}
        data={feedbackList}
        columns={columns}
        // Add button and filter toggle only for admin / manager
        onAddBtnClick={isPrivileged ? () => setIsAddReview(true) : undefined}
        leftSideButtons={reviewFilterButtons}
        searchWidth="lg:w-48 xl:w-48"
        loading={loading}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage + 1}
        totalItems={totalItems}
        // "My Feedback" is filtered client-side from getAll, so it uses local pagination
        serverSidePagination={isPrivileged && activeView !== "receivedByMe"}
        isExportable={isPrivileged}
        onPageChange={handleServerPageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        onSearchTermChange={handleSearchChange}
        searchDebounceMs={500}
        searchAutoComplete="off"
      />

      {/* Add Review — privileged only */}
      {isPrivileged && isAddReview && (
        <GenericModal isOpen={isAddReview} onClose={() => setIsAddReview(false)} header="Add Review">
          <AddReview
            onClose={() => setIsAddReview(false)}
            onSuccess={handleSuccess}
            onError={handleError}
          />
        </GenericModal>
      )}

      {/* View Review */}
      {view && (
        <GenericModal isOpen={view} onClose={() => setView(false)} header="View Review">
          <ViewReview data={editData} />
        </GenericModal>
      )}

      {/* Edit Review — privileged only */}
      {isPrivileged && edit && (
        <GenericModal isOpen={edit} onClose={() => setEdit(false)} header="Edit Review">
          <EditReview
            onClose={() => setEdit(false)}
            onSuccess={handleSuccess}
            onError={handleError}
            editData={editData}
            isViewMode={false}
          />
        </GenericModal>
      )}

      {/* Delete — privileged only */}
      {isPrivileged && isDelete && (
        <GenericModal isOpen={isDelete} onClose={() => setIsDelete(false)} header="Delete Review">
          <DeletePopup
            handleDelete={deleteRow}
            onClose={() => setIsDelete(false)}
            selectedRow={selectedRow}
          />
        </GenericModal>
      )}

      {/* Toaster */}
      {(showSuccessToaster || showErrorToaster) && (
        <GenericToaster
          message={message}
          onClose={closeToaster}
          open={showSuccessToaster || showErrorToaster}
          severity={showErrorToaster ? "error" : "info"}
        />
      )}
    </div>
  );
};

export default ReviewList;
